import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Mail, Search, RefreshCw, Send, CheckCircle2, XCircle, Ticket, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function TicketResendManager() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchEventId, setSearchEventId] = useState('');
  const [statusFilter, setStatusFilter] = useState('confirmed');
  const [selected, setSelected] = useState(new Set());
  const [resendDialog, setResendDialog] = useState(null);
  const [overrideEmail, setOverrideEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchEmail) params.append('email', searchEmail);
      if (searchEventId) params.append('event_id', searchEventId);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '50');
      const res = await axios.get(`${API_URL}/api/admin/tickets/search?${params}`);
      setTickets(res.data.tickets || []);
      setSelected(new Set());
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [searchEmail, searchEventId, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchTickets, 300);
    return () => clearTimeout(timer);
  }, [fetchTickets]);

  const handleResendSingle = async () => {
    if (!resendDialog) return;
    setSending(true);
    try {
      const body = overrideEmail ? { override_email: overrideEmail } : {};
      const res = await axios.post(`${API_URL}/api/admin/tickets/${resendDialog.ticket_id}/resend-email`, body);
      toast.success(`Email sent to ${res.data.recipient_email}`);
      setResendDialog(null);
      setOverrideEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleBulkResend = async () => {
    if (selected.size === 0) return;
    setBulkSending(true);
    try {
      const body = { ticket_ids: Array.from(selected) };
      if (overrideEmail) body.override_email = overrideEmail;
      const res = await axios.post(`${API_URL}/api/admin/tickets/resend-bulk`, body);
      const { succeeded, failed } = res.data;
      if (succeeded > 0) toast.success(`${succeeded} email(s) sent`);
      if (failed > 0) toast.error(`${failed} email(s) failed`);
      setSelected(new Set());
      setOverrideEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk resend failed');
    } finally {
      setBulkSending(false);
    }
  };

  const toggleSelect = (ticketId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(ticketId) ? next.delete(ticketId) : next.add(ticketId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === tickets.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(tickets.map(t => t.ticket_id)));
    }
  };

  return (
    <div className="space-y-4" data-testid="ticket-resend-manager">
      {/* Search Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground mb-1 block">Email</label>
          <Input placeholder="Search by email..." value={searchEmail} onChange={e => setSearchEmail(e.target.value)} data-testid="ticket-search-email" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground mb-1 block">Event ID</label>
          <Input placeholder="evt_..." value={searchEventId} onChange={e => setSearchEventId(e.target.value)} data-testid="ticket-search-event" />
        </div>
        <div className="w-[150px]">
          <label className="text-xs text-muted-foreground mb-1 block">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="ticket-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTickets} disabled={loading} data-testid="ticket-search-btn">
          <Search className="w-4 h-4 mr-1" /> Search
        </Button>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-3 px-4 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium">{selected.size} ticket(s) selected</span>
            <div className="flex items-center gap-2">
              <Input placeholder="Override email (optional)" value={overrideEmail} onChange={e => setOverrideEmail(e.target.value)} className="w-[250px] h-8 text-sm" data-testid="bulk-override-email" />
              <Button size="sm" onClick={handleBulkResend} disabled={bulkSending} data-testid="bulk-resend-btn">
                <Send className="w-4 h-4 mr-1" /> {bulkSending ? 'Sending...' : `Resend ${selected.size} Email(s)`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tickets Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Ticket className="w-5 h-5" /> Tickets ({tickets.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchTickets} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-8">
              <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">{loading ? 'Searching...' : 'No tickets found'}</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[500px] overflow-y-auto" data-testid="ticket-list">
              {/* Header */}
              <div className="flex items-center gap-3 py-2 px-3 text-xs font-medium text-muted-foreground border-b">
                <Checkbox checked={selected.size === tickets.length && tickets.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all-tickets" />
                <span className="w-[140px]">Ticket ID</span>
                <span className="flex-1 min-w-[120px]">Email</span>
                <span className="w-[140px]">Event</span>
                <span className="w-[80px]">Type</span>
                <span className="w-[70px]">Status</span>
                <span className="w-[90px]">Created</span>
                <span className="w-[80px]">Action</span>
              </div>
              {tickets.map(ticket => {
                const email = ticket.user_email || ticket.guest_email || '';
                const isSelected = selected.has(ticket.ticket_id);
                return (
                  <div key={ticket.ticket_id} className={`flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''}`} data-testid={`ticket-row-${ticket.ticket_id}`}>
                    <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(ticket.ticket_id)} />
                    <span className="w-[140px] font-mono text-xs truncate" title={ticket.ticket_id}>{ticket.ticket_id}</span>
                    <span className="flex-1 min-w-[120px] text-sm truncate" title={email}>
                      {email || <span className="text-muted-foreground italic">No email</span>}
                    </span>
                    <span className="w-[140px] text-xs truncate" title={ticket.event_title}>{ticket.event_title || ticket.event_id}</span>
                    <span className="w-[80px] text-xs">{ticket.ticket_type_name || '-'}</span>
                    <span className="w-[70px]">
                      <Badge variant={ticket.status === 'confirmed' ? 'default' : 'outline'} className={`text-xs ${ticket.status === 'confirmed' ? 'bg-emerald-500 text-white' : ''}`}>
                        {ticket.status}
                      </Badge>
                    </span>
                    <span className="w-[90px] text-xs text-muted-foreground">
                      {ticket.created_at ? formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true }).replace('about ', '') : '-'}
                    </span>
                    <span className="w-[80px]">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setResendDialog(ticket); setOverrideEmail(''); }} data-testid={`resend-btn-${ticket.ticket_id}`}>
                        <Mail className="w-3.5 h-3.5 mr-1" /> Resend
                      </Button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resend Confirmation Dialog */}
      <Dialog open={!!resendDialog} onOpenChange={(open) => { if (!open) setResendDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend Ticket Email</DialogTitle>
          </DialogHeader>
          {resendDialog && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <p><span className="text-muted-foreground">Ticket:</span> <span className="font-mono">{resendDialog.ticket_id}</span></p>
                <p><span className="text-muted-foreground">Event:</span> {resendDialog.event_title || resendDialog.event_id}</p>
                <p><span className="text-muted-foreground">Type:</span> {resendDialog.ticket_type_name}</p>
                <p>
                  <span className="text-muted-foreground">Email on file:</span>{' '}
                  {resendDialog.user_email || resendDialog.guest_email || <span className="text-rose-500">None found</span>}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Override Email (optional)</label>
                <Input value={overrideEmail} onChange={e => setOverrideEmail(e.target.value)} placeholder="Enter email to override..." data-testid="resend-override-email" />
                <p className="text-xs text-muted-foreground mt-1">Leave empty to use the email on file. Required if no email found.</p>
              </div>
              {!resendDialog.user_email && !resendDialog.guest_email && !overrideEmail && (
                <div className="flex items-center gap-2 text-sm text-amber-500">
                  <AlertTriangle className="w-4 h-4" /> No email on file — you must provide an override email.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendDialog(null)}>Cancel</Button>
            <Button onClick={handleResendSingle} disabled={sending || (!resendDialog?.user_email && !resendDialog?.guest_email && !overrideEmail)} data-testid="confirm-resend-btn">
              <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
